import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';

interface Props {
  activityId: string;
}

interface FillResult {
  added: number;
  ratingMinPlayers: number;
  ratingWillCount: boolean;
}

interface ResultResult {
  rating: string;
  ratingSkipReason: string | null;
  mvpA: string | null;
  mvpB: string | null;
}

/**
 * Набить игру ботами → расставить составы → проставить счёт.
 * Ручки живут только на dev-стенде; на проде /demo/enabled отдаёт 404,
 * и блок не рендерится.
 */
export function DemoToolsCard({ activityId }: Props) {
  const qc = useQueryClient();
  const [count, setCount] = useState(12);
  const [scoreA, setScoreA] = useState(3);
  const [scoreB, setScoreB] = useState(2);
  const [note, setNote] = useState<string | null>(null);

  const enabled = useQuery({
    queryKey: ['admin', 'demo', 'enabled'],
    queryFn: () => api.get<{ data: { enabled: boolean; maxBots: number } }>('/api/admin/demo/enabled'),
    retry: false,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'activity', activityId] });
  };

  const fill = useMutation({
    mutationFn: () =>
      api.post<{ data: FillResult }>(`/api/admin/activities/${activityId}/demo/fill`, { count }),
    onSuccess: (res) => {
      const d = res.data;
      setNote(
        d.ratingWillCount
          ? `Добавлено ${d.added}. Состав добран — рейтинг начислится.`
          : `Добавлено ${d.added}, но для рейтинга нужно ${d.ratingMinPlayers}. Сейчас игра нерейтинговая.`,
      );
      refresh();
    },
    onError: (e: Error) => setNote(e.message),
  });

  const shuffle = useMutation({
    mutationFn: () => api.post(`/api/admin/activities/${activityId}/demo/shuffle`, {}),
    onSuccess: () => { setNote('Составы расставлены (snake draft).'); refresh(); },
    onError: (e: Error) => setNote(e.message),
  });

  const result = useMutation({
    mutationFn: () =>
      api.post<{ data: ResultResult }>(`/api/admin/activities/${activityId}/demo/result`, {
        scoreA, scoreB, withMvp: true,
      }),
    onSuccess: (res) => {
      const d = res.data;
      setNote(
        d.rating === 'rated'
          ? 'Игра завершена, рейтинг начислен, MVP выбраны.'
          : `Игра завершена, но рейтинг не начислен: ${d.ratingSkipReason ?? d.rating}.`,
      );
      refresh();
    },
    onError: (e: Error) => setNote(e.message),
  });

  const clear = useMutation({
    mutationFn: () => api.delete<{ data: { removed: number } }>(`/api/admin/activities/${activityId}/demo/bots`),
    onSuccess: (res) => { setNote(`Ботов убрано из игры: ${res.data.removed}.`); refresh(); },
    onError: (e: Error) => setNote(e.message),
  });

  // Ручка 404-ит на проде — блок просто не показываем.
  if (enabled.isError || !enabled.data?.data.enabled) return null;

  const maxBots = enabled.data.data.maxBots;
  const busy = fill.isPending || shuffle.isPending || result.isPending || clear.isPending;

  return (
    <Card className="p-4 space-y-4 border-dashed">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Демо-инструменты</h3>
        <Badge variant="outline">только dev</Badge>
      </div>
      <p className="text-sm text-muted-foreground">
        Набить игру ботами, расставить составы, завершить со счётом — для скриншотов в сторы.
      </p>

      <div className="flex items-end gap-2">
        <div className="w-28">
          <Label htmlFor="demo-count">Ботов</Label>
          <Input
            id="demo-count"
            type="number"
            min={1}
            max={maxBots}
            value={count}
            onChange={(e) => setCount(Number(e.target.value))}
          />
        </div>
        <Button onClick={() => fill.mutate()} disabled={busy}>
          {fill.isPending ? 'Добавляю…' : 'Набить'}
        </Button>
        <Button variant="secondary" onClick={() => shuffle.mutate()} disabled={busy}>
          {shuffle.isPending ? 'Тасую…' : 'Расставить составы'}
        </Button>
      </div>

      <div className="flex items-end gap-2">
        <div className="w-20">
          <Label htmlFor="demo-a">Счёт A</Label>
          <Input id="demo-a" type="number" min={0} value={scoreA} onChange={(e) => setScoreA(Number(e.target.value))} />
        </div>
        <div className="w-20">
          <Label htmlFor="demo-b">Счёт B</Label>
          <Input id="demo-b" type="number" min={0} value={scoreB} onChange={(e) => setScoreB(Number(e.target.value))} />
        </div>
        <Button onClick={() => result.mutate()} disabled={busy}>
          {result.isPending ? 'Завершаю…' : 'Завершить + рейтинг + MVP'}
        </Button>
        <Button variant="destructive" onClick={() => clear.mutate()} disabled={busy}>
          Убрать ботов
        </Button>
      </div>

      {note && <p className="text-sm">{note}</p>}
    </Card>
  );
}
